import type { PageSize } from '@/lib/table/types';
import type { SortOrder } from '@/lib/table/types';

const VALID_PAGE_SIZES: PageSize[] = [10, 25, 50, 100];

export interface BaseSearchParams<TTab extends string> {
  search?: string;
  page?: number;
  pageSize?: PageSize;
  tab?: TTab;
  sortBy?: string;
  sortOrder?: SortOrder;
}

export type SearchParamsWithFilters<
  TFilters extends Record<string, string | undefined>,
  TTab extends string,
> = BaseSearchParams<TTab> & TFilters;

export interface CreateSearchValidatorOptions<
  TFilters extends Record<string, string | undefined>,
  TTab extends string,
> {
  tabs: readonly TTab[];
  filterKeys: (keyof TFilters & string)[];
  /**
   * The sort keys the backend accepts. When given, a `sortBy` outside this set
   * is dropped (with its `sortOrder`) instead of forwarded. Without persistence
   * a bad `sortBy` only came from a hand-edited URL; once a stored view can
   * carry one, a since-removed sort column would otherwise be re-sent on every
   * entry and 400 forever. Omit to keep the old pass-through behaviour.
   */
  sortKeys?: readonly string[];
}

export function createSearchValidator<TFilters extends Record<string, string | undefined>, TTab extends string>({
  tabs,
  filterKeys,
  sortKeys,
}: CreateSearchValidatorOptions<TFilters, TTab>) {
  return (search: Record<string, unknown>): SearchParamsWithFilters<TFilters, TTab> => {
    const page = Number(search.page);
    const pageSize = Number(search.pageSize);
    const tab = typeof search.tab === 'string' ? search.tab : undefined;

    const rawSortBy = typeof search.sortBy === 'string' ? search.sortBy : undefined;
    const sortBy =
      rawSortBy !== undefined && (sortKeys === undefined || sortKeys.includes(rawSortBy)) ? rawSortBy : undefined;
    const sortOrder =
      sortBy !== undefined && (search.sortOrder === 'asc' || search.sortOrder === 'desc')
        ? search.sortOrder
        : undefined;

    const result: SearchParamsWithFilters<TFilters, TTab> = {
      search: typeof search.search === 'string' ? search.search : undefined,
      page: Number.isNaN(page) || !Number.isInteger(page) || page < 0 ? 0 : page,
      pageSize: VALID_PAGE_SIZES.includes(pageSize as PageSize) ? (pageSize as PageSize) : undefined,
      tab: tabs.includes(tab as TTab) ? (tab as TTab) : undefined,
      sortBy,
      sortOrder,
    } as SearchParamsWithFilters<TFilters, TTab>;

    const filters = {} as Record<keyof TFilters, string | undefined>;

    for (const key of filterKeys) {
      const value = search[key];
      if (typeof value === 'string') {
        filters[key] = value;
      }
    }

    return {
      ...result,
      ...filters,
    };
  };
}
